<template>
    <v-row dense>
        <v-col cols="12" md="12">
            <v-card variant="outlined" :loading="isTariffsLoading">
                <v-card-title>
                    <span class="font-weight-bold text-subtitle-2">
                        {{ $t('discount_period') }}
                    </span>
                </v-card-title>
                <v-card-text>
                    <v-data-table
                        :items="discountForEdit.tariffLinks"
                        :headers="headersTariffs"
                        :hide-default-header="isMobile"
                        hide-default-footer
                        :items-per-page="-1"
                    >
                        <template v-slot:item.tariff="{item}">
                            <div class="my-3">
                                <v-select
                                    v-model="item.tariffId"
                                    :items="tariffs"
                                    item-value="id"
                                    item-title="name"
                                    hide-details
                                    :density="'compact'"
                                />
                            </div>
                        </template>
                        <template v-slot:item.dates="{item}">
                            <div class="d-flex align-center">
                                <div class="me-4 d-flex flex-column py-3">
                                    <date-picker
                                        v-model="item.dateFrom"
                                        outlined
                                        :density="'compact'"
                                        :max="item.dateTo || '2100-01-01'"
                                        :label="$t('date_from') + '*'"
                                        :rules="[requiredRule('date_from')]"
                                        hide-details
                                    />
                                    <div class="py-1" />
                                    <date-picker
                                        v-model="item.dateTo"
                                        outlined
                                        :min="item.dateFrom || '1900-01-01'"
                                        :density="'compact'"
                                        :label="$t('date_to') + '*'"
                                        :rules="[requiredRule('date_to')]"
                                        hide-details
                                    />
                                </div>
                            </div>
                        </template>
                        <template v-slot:item.discountCategory="{item}">
                            <v-radio-group v-model="item.discountCategory" hide-details>
                                <v-radio
                                    v-for="discountCategory in discountCategoryItems"
                                    :key="discountCategory.value"
                                    :value="discountCategory.value"
                                    :label="discountCategory.title"
                                />
                            </v-radio-group>
                        </template>
                        <template v-slot:item.applicability="{item}">
                            <v-radio-group v-model="item.applicability" hide-details>
                                <v-radio
                                    v-for="discountApplicability in discountApplicabilityItems"
                                    :key="discountApplicability.value"
                                    :value="discountApplicability.value"
                                    :label="discountApplicability.title"
                                />
                            </v-radio-group>
                        </template>
                        <template v-slot:item.actions="{item, index}">
                            <div class="full-width d-flex align-center">
                                <v-spacer />
                                <v-btn variant="text" size="small" color="primary" @click="copyTariffLink(item)">
                                    {{ $t('copy') }}
                                </v-btn>
                                <v-btn
                                    size="small"
                                    color="error"
                                    variant="text"
                                    icon="mdi-close"
                                    @click="removeTariffLink(index)"
                                />
                            </div>
                        </template>
                        <template v-slot:item.mobile="{item, index}">
                            <v-col cols="12" class="mt-4 px-0">
                                <v-select
                                    v-model="item.tariffId"
                                    :items="tariffs"
                                    item-value="id"
                                    item-title="name"
                                    hide-details
                                    :density="'compact'"
                                />
                            </v-col>
                            <v-col cols="12" class="px-0">
                                <date-picker
                                    v-model="item.dateFrom"
                                    outlined
                                    :density="'compact'"
                                    :max="item.dateTo || '2100-01-01'"
                                    :label="$t('date_from') + '*'"
                                    :rules="[requiredRule('date_from')]"
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="12" class="px-0">
                                <date-picker
                                    v-model="item.dateTo"
                                    outlined
                                    :min="item.dateFrom || '1900-01-01'"
                                    :density="'compact'"
                                    :label="$t('date_to') + '*'"
                                    :rules="[requiredRule('date_to')]"
                                    hide-details
                                />
                            </v-col>
                            <v-col v-if="hasAccess('show.contract.price.discountCategory')" cols="12" class="px-0">
                                <v-radio-group v-model="item.discountCategory" hide-details>
                                    <v-radio
                                        v-for="discountCategory in discountCategoryItems"
                                        :key="discountCategory.value"
                                        :value="discountCategory.value"
                                        :label="discountCategory.title"
                                    />
                                </v-radio-group>
                                <v-divider />
                            </v-col>
                            <v-col cols="12" class="px-0">
                                <v-radio-group v-model="item.applicability" hide-details>
                                    <v-radio
                                        v-for="discountApplicability in discountApplicabilityItems"
                                        :key="discountApplicability.value"
                                        :value="discountApplicability.value"
                                        :label="discountApplicability.title"
                                    />
                                </v-radio-group>
                                <v-divider />
                            </v-col>
                            <v-col cols="12" class="px-0">
                                <div class="full-width d-flex align-center">
                                    <v-spacer />
                                    <v-btn variant="text" size="small" color="primary" @click="copyTariffLink(item)">
                                        {{ $t('copy') }}
                                    </v-btn>
                                    <v-btn
                                        size="small"
                                        color="error"
                                        variant="text"
                                        icon="mdi-close"
                                        @click="removeTariffLink(index)"
                                    />
                                </div>
                            </v-col>
                        </template>
                    </v-data-table>
                    <v-divider class="mb-4" />
                    <v-row dense>
                        <v-col cols="12">
                            <div class="full-width d-flex align-center">
                                <v-btn size="small" :disabled="!tariffs?.length" @click="addTariffLink">
                                    {{ $t('add') }}
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
    import DatePicker from '~/components/snippets/forms/DatePicker.vue'

    const props = defineProps<{
        isTariffsLoading: boolean
        tariffs: any[]
    }>()

    const discountForEdit = defineModel<any>()

    const {$breakpoint} = useNuxtApp()
    const {t} = useI18n()
    const {hasAccess} = useAccess()

    const headersTariffs = computed(() => {
        if (isMobile.value) {
            return [{title: '', value: 'mobile'}]
        }
        return [
            {title: t('tariff'), value: 'tariff'},
            {title: t('dates'), value: 'dates'},
            {title: t('apply_discount'), value: 'applicability'},
            ...(hasAccess('show.contract.price.discountCategory')
                ? [{title: t('discount_type'), value: 'discountCategory'}]
                : []),
            {title: t('actions'), value: 'actions', align: 'end'},
        ]
    })

    const isMobile = computed(() => $breakpoint.smAndDown)

    const discountCategoryItems = computed(() =>
        ['SUPPLIER', 'TO'].map(el => ({
            title: t(`discountCategory.${el}`),
            value: el,
        }))
    )

    const discountApplicabilityItems = computed(() =>
        ['OPTIONAL', 'ALWAYS'].map(el => ({
            title: t(`discountApplicability.${el}`),
            value: el,
        }))
    )

    const addTariffLink = () => {
        const tariff = props.tariffs?.[0]
        discountForEdit.value.tariffLinks.push({
            tariffId: tariff ? tariff.id : null,
            dateFrom: tariff ? tariff.dateFrom : null,
            dateTo: tariff ? tariff.dateTo : null,
            priority: discountForEdit.value.tariffLinks?.length + 1,
            discountCategory: discountCategoryItems.value[0].value,
            applicability: discountApplicabilityItems.value[0].value,
        })
    }

    const copyTariffLink = tariffLink => {
        discountForEdit.value.tariffLinks.push({
            tariffId: tariffLink.tariffId,
            dateFrom: tariffLink.dateFrom,
            dateTo: tariffLink.dateTo,
            priority: discountForEdit.value.tariffLinks?.length + 1,
            discountCategory: tariffLink.discountCategory,
            applicability: tariffLink.applicability,
        })
    }

    const removeTariffLink = tariffLinkIndex => {
        discountForEdit.value.tariffLinks.splice(tariffLinkIndex, 1)
    }

    defineExpose({
        addTariffLink,
    })
</script>
